import type { Dispatch, SetStateAction } from "react"
import { useCallback, useEffect, useRef, useState } from "react"

import { useAppDispatch, useAppSelector, useDeepEffect } from "@/core/app/hooks"
import { RootState } from "@/core/app/store"
import { setAlert } from "@/core/app/slices/alert/alertSlice"
import {
  fetchScannerPatientList,
  refreshScannerPatients,
} from "@/core/app/slices/integrations/integrationsThunkApi"
import { setPatientListLatestSyncTimestamp } from "@/core/app/slices/integrations"
import {
  type ScannerPatientItem,
  RefreshPatientsResponse,
} from "@/core/app/slices/integrations/integrations.type"
import {
  SCANNER_REFRESH_REASON,
  SCANNER_SYNC_STATUS,
} from "@/core/app/slices/integrations/scannerConstants"
import type { ScannerIntegrationConfig, ScannerListParams } from "./scannerIntegrations.config"
import { useRelativeTimestamp } from "./useRelativeTimestamp"

/** Map API patient rows to the table shape shared with device-scanner rows. */
function mapIntegrationPatientsToFilterRows(patients: ScannerPatientItem[]) {
  return patients.map((items) => {
    const withLocation = items as typeof items & {
      location?: string
      created_time?: string
    }
    return {
      Info: {
        CaseId: items.external_case_id,
        FirstName: items.patient_first_name,
        LastName: items.patient_last_name,
        DOB: items.patient_dob,
        PatientName: `${items.patient_first_name} ${items.patient_last_name}`,
        CreateTime: withLocation.created_time,
        location: withLocation.location,
        notes: items.notes,
      },
    }
  })
}

const isSelectedIntegration = (
  type: string,
  config: ScannerIntegrationConfig | null,
) => config !== null && type === config.key

/** Polls backend integration list while sync is in progress; drives Sync Now / last-sync UI. */
export function useIntegrationPatientList(options: {
  scannerConfig: ScannerIntegrationConfig | null
  showScannerPatients: boolean
  /** When false, list fetch/poll/sync must not run (disconnected or suppressed in UI). */
  integrationListAllowed: boolean
  listParams: ScannerListParams
  searchText: string
  rowsPerPage: number
  scannerSelect: string
  setFilterList: Dispatch<SetStateAction<unknown[]>>
}) {
  const {
    scannerConfig,
    showScannerPatients,
    integrationListAllowed,
    listParams,
    searchText,
    rowsPerPage,
    scannerSelect,
    setFilterList,
  } = options

  const dispatch = useAppDispatch()
  const { patientListDetails } = useAppSelector(
    (state: RootState) => state.integrationsService,
  )
  const prescriptionId = useAppSelector(
    (state: RootState) => state.caseService.caseDetail?.extra_info?.easyrx?.prescription_id,
  )

  const [syncNow, setSyncNow] = useState<"HIDDEN" | "SHOWN" | "SYNCING">(
    "HIDDEN",
  )
  /** Bumps on search/page/close so in-flight polls skip setFilterList (stale response guard). */
  const pollSessionRef = useRef(0)
  /** Holds the active poll-interval timer ID so it can be cancelled on unmount / session change. */
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const lastSyncedRelative = useRelativeTimestamp(
    patientListDetails.latest_sync_timestamp,
    showScannerPatients,
  )

  useEffect(() => {
    if (!showScannerPatients) {
      setSyncNow("HIDDEN")
      pollSessionRef.current += 1
      if (pollTimerRef.current !== null) {
        clearTimeout(pollTimerRef.current)
        pollTimerRef.current = null
      }
    }
  }, [showScannerPatients])

  useEffect(() => {
    if (!showScannerPatients) {
      return
    }
    if (!isSelectedIntegration(scannerSelect, scannerConfig) || !integrationListAllowed) {
      return
    }
    // Delay before showing “Sync Now” so first paint isn’t cluttered.
    const t = globalThis.setTimeout(() => {
      setSyncNow("SHOWN")
    }, 5000)
    return () => globalThis.clearTimeout(t)
  }, [showScannerPatients, scannerSelect, scannerConfig, integrationListAllowed])

  const pollUntilScannerListSynced = useCallback(async () => {
    if (
      !scannerConfig ||
      !isSelectedIntegration(listParams.type, scannerConfig) ||
      !showScannerPatients ||
      !integrationListAllowed
    ) {
      return
    }

    const session = ++pollSessionRef.current

    while (session === pollSessionRef.current) {
      // Loop until sync completes or session invalidated (deps changed / dialog closed).
      try {
        const payload = await dispatch(
          fetchScannerPatientList({
            scannerName: scannerConfig.apiName,
            search: searchText.trim() || null,
            page: listParams.page + 1,
            page_size: rowsPerPage,
            location: listParams.location,
            isPolling: true,
            ...(prescriptionId ? { external_case_id: prescriptionId } : {}),
          }),
        ).unwrap()

        const syncStatus = payload.sync_status

        if (session !== pollSessionRef.current) {
          break
        }

        if (
          !showScannerPatients ||
          syncStatus === SCANNER_SYNC_STATUS.COMPLETED
        ) {
          setFilterList(mapIntegrationPatientsToFilterRows(payload.patients ?? []))
          setSyncNow((prev) => (prev === "SYNCING" ? "SHOWN" : prev))
          break
        }

        await new Promise<void>((resolve) => {
          pollTimerRef.current = setTimeout(resolve, 2000) // poll interval while sync_status in_progress
        })
        pollTimerRef.current = null
      } catch {
        setSyncNow((prev) => (prev === "SYNCING" ? "SHOWN" : prev))
        break
      }
    }
  }, [
    scannerConfig,
    listParams.type,
    listParams.page,
    listParams.location,
    showScannerPatients,
    integrationListAllowed,
    searchText,
    rowsPerPage,
    dispatch,
    setFilterList,
  ])

  useDeepEffect(() => {
    if (
      scannerConfig &&
      isSelectedIntegration(listParams.type, scannerConfig) &&
      showScannerPatients &&
      integrationListAllowed
    ) {
      pollUntilScannerListSynced()
    }

    return () => {
      pollSessionRef.current += 1
      if (pollTimerRef.current !== null) {
        clearTimeout(pollTimerRef.current)
        pollTimerRef.current = null
      }
    }
  }, [
    scannerConfig,
    listParams.type,
    listParams.page,
    listParams.location,
    showScannerPatients,
    integrationListAllowed,
    searchText,
    rowsPerPage,
  ])

  const syncNowClickHandler = useCallback(() => {
    if (
      !scannerConfig ||
      !isSelectedIntegration(listParams.type, scannerConfig) ||
      !integrationListAllowed
    ) {
      return
    }
    setSyncNow("SYNCING")
    dispatch(refreshScannerPatients({ scannerName: scannerConfig.apiName }))
      .unwrap()
      .then((response: RefreshPatientsResponse) => {
        const isAlreadyUpToDate =
          response.reason === SCANNER_REFRESH_REASON.ALREADY_UP_TO_DATE

        if (isAlreadyUpToDate) {
          const syncMessage = response.sync_message?.trim()
          if (syncMessage) {
            dispatch(setAlert({ message: syncMessage, severity: "success" }))
          }
          if (response.last_sync_timestamp != null) {
            dispatch(
              setPatientListLatestSyncTimestamp(response.last_sync_timestamp),
            )
          }
          setSyncNow("SHOWN")
          return
        }

        pollUntilScannerListSynced()
      })
      .catch((err) => {
        setSyncNow("SHOWN")
        console.error(err)
      })
  }, [
    dispatch,
    scannerConfig,
    listParams.type,
    integrationListAllowed,
    pollUntilScannerListSynced,
  ])

  const clearIntegrationTableRows = useCallback(() => {
    setFilterList([])
  }, [setFilterList])

  return {
    syncNow,
    lastSyncedRelative,
    patientListDetails,
    syncNowClickHandler,
    clearIntegrationTableRows,
  }
}
